import type { ITextField } from '@/typescript/interfaces/ITextField';

/**
 * Convert a flat character index into a { line, char } coordinate inside a text field.
 *
 * The index is captured from the fabric object while the coordinate is resolved against the text
 * stored in the template, so both can disagree for a short while. The index is therefore clamped
 * to the stored text: the walk always lands on an existing line, and `char` never exceeds the
 * length of that line.
 */
function getTextFieldFirstCharacterCoordinate(textField: ITextField, position: number) {
    if (!textField.text) {
        return { char: 0, line: 0 };
    }

    const textSplittedByLines = textField.text.split('\n');
    let cursor = Math.min(Math.max(position, 0), textField.text.length);
    let currentLine = 0;

    // The clamp above already makes the bounds check unreachable: the line lengths sum back to the
    // clamped range, so the walk always stops on the last line. It is kept as a backstop because
    // the callers are Vue computed getters, where a throw breaks the render instead of just
    // returning a wrong coordinate.
    while (currentLine < textSplittedByLines.length - 1 && cursor > textSplittedByLines[currentLine].length) {
        cursor -= textSplittedByLines[currentLine].length + 1;
        currentLine++;
    }

    return { char: cursor, line: currentLine };
}

export { getTextFieldFirstCharacterCoordinate };
